import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useState, useCallback, useMemo, useRef, useEffect, type CSSProperties } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AppLayout from "@/components/layout/AppLayout";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import SpaceCanvasSection from "@/components/spaces/SpaceCanvasSection";
import { fireConfetti } from "@/components/spaces/JournalTodoList";
import MorphingPill from "@/components/chat/MorphingPill";
import AgentTranscript, { type TranscriptMessage } from "@/components/chat/TranscriptPanel";
import AmbientWave, { type AmbientState } from "@/components/chat/AmbientWave";
import { useJournal } from "@/hooks/useJournal";
import { useJournalBlocks, useCreateBlock, useReorderBlocks, useDeleteBlock, useDebouncedBlockContentUpdate } from "@/hooks/useJournalBlocks";
import { useLiveAdapter } from "@/hooks/useLiveAdapter";
import { useJournalContext } from "@/hooks/useJournalContext";
import { useUserMemories } from "@/hooks/useUserMemories";
import { Loader2 } from "lucide-react";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import { MobileSpaceView, type MobileBlock } from "@/components/mobile";

const SpaceDetailPage = () => {
  const { journalId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isTabletOrMobile = useIsTabletOrMobile();
  const queryClient = useQueryClient();
  const autoConnectTriggeredRef = useRef(false);

  // Fetch the journal and blocks from database
  const { data: journal, isLoading } = useJournal(journalId);
  const { data: blocks } = useJournalBlocks(journalId);

  // Block mutation hooks for mobile
  const createBlockMutation = useCreateBlock();
  const reorderBlocksMutation = useReorderBlocks();
  const deleteBlockMutation = useDeleteBlock();
  const debouncedContentUpdate = useDebouncedBlockContentUpdate(journalId);

  // Phase 8a: Fetch user memories for agent personalization
  const { data: userMemories } = useUserMemories();

  // Vision integration state - for AI agent to "see" the canvas
  const [getCanvasEl, setGetCanvasEl] = useState<(() => HTMLElement | null) | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);

  // Active block tracking for context updates
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);

  // UI state - single toggle for transcript panel on right
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(true);
  const [hasCanvasContent, setHasCanvasContent] = useState(false);
  const [chatInputText, setChatInputText] = useState('');
  const [inputMode, setInputMode] = useState<'talk' | 'chat'>('talk');
  const [autoExpandBlockId, setAutoExpandBlockId] = useState<string | null>(null);

  // Message state for transcript
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [unsavedMessageCount, setUnsavedMessageCount] = useState(0);
  const messageSeqRef = useRef(0);

  // Phase 8a: Use ref to provide stable getMessages callback for transcript sync
  // This prevents the useEffect in useMultimodalLive from constantly resetting
  const messagesRef = useRef<TranscriptMessage[]>(messages);
  messagesRef.current = messages; // Keep ref in sync with state
  const getMessages = useCallback(() => messagesRef.current, []);

  // Append message helper
  const appendMessage = useCallback((message: TranscriptMessage) => {
    setMessages(prev => [...prev, message]);
    setUnsavedMessageCount(prev => prev + 1);
  }, []);

  // Handle AI agent creating a block
  const handleBlockAdded = useCallback((data: { block_id: string; block_type: string; title?: string }) => {
    console.log('📝 AI agent created block:', data);
    // Refetch blocks to show the new block
    queryClient.invalidateQueries({ queryKey: ['journal-blocks', journalId] });

    // Format block type for display
    const typeLabels: Record<string, string> = {
      note: 'Note',
      quiz: 'Quiz',
      youtube: 'YouTube Video',
      explainer: 'Explainer',
    };
    const blockLabel = data.title || typeLabels[data.block_type] || 'Block';

    // Show a subtle toast
    toast.success(`Added "${blockLabel}" to journal`, {
      duration: 3000,
    });
  }, [queryClient, journalId]);

  // Handle AI agent modifying todos (Phase 6)
  const handleJournalUpdated = useCallback((data: { journal_id: string; action: string }) => {
    console.log('📋 AI agent updated journal todos:', data);
    // Refetch journal to update the todo list
    queryClient.invalidateQueries({ queryKey: ['journal', journalId] });

    // Show a toast based on action
    if (data.action === 'add_todo') {
      toast.success('Added learning goal to your plan', { duration: 2000 });
    } else if (data.action === 'mark_todo_complete') {
      toast.success('Marked goal as complete! 🎉', { duration: 2000 });
      // Trigger confetti celebration for agent-completed todos
      fireConfetti();
    } else if (data.action === 'batch_update') {
      toast.success('Updated your learning plan', { duration: 2000 });
    }
  }, [queryClient, journalId]);

  // Handle AI agent editing blocks (Phase 7)
  const handleBlockUpdated = useCallback((data: { block_id: string }) => {
    console.log('📝 AI agent updated block:', data);
    // Refetch blocks to show updated content
    queryClient.invalidateQueries({ queryKey: ['journal-blocks', journalId] });
    toast.success('Updated note', { duration: 2000 });
  }, [queryClient, journalId]);

  // Real agent integration using useLiveAdapter
  const geminiLive = useLiveAdapter({
    topicId: journalId,              // Use journalId as topic for backend context
    setMessages,
    setUnsavedMessageCount,
    messageSeqRef,
    getCanvasEl,                     // Vision capture - canvas viewport
    screenStream,                    // Screen share fallback
    appendMessage,
    onBlockAdded: handleBlockAdded,    // Handle AI-created blocks
    onBlockUpdated: handleBlockUpdated, // Handle AI-updated blocks
    onJournalUpdated: handleJournalUpdated, // Handle todo list updates
    // Phase 8a: Session summarization - use stable getMessages callback
    journalId,
    getMessages,
  });

  // Journal context integration - sends structured context to agent
  // Phase 8: Includes user memories and session history for personalization
  useJournalContext({
    journal: journal ? {
      id: journal.id,
      name: journal.name,
      summary: journal.summary,
      tutor_instructions: journal.tutor_instructions,
      todo_items: (journal.todo_items as unknown) as import('@/types/journal').TodoItem[] | null,
      // Phase 8b: Session history for continuity (type assertion until Supabase types regenerated)
      session_history: ((journal as any).session_history as import('@/hooks/useJournalContext').SessionSummary[] | null) ?? null,
    } : null,
    blocks,
    activeBlockId,
    sendContextUpdate: geminiLive.sendContextUpdate,
    isAgentConnected: geminiLive.agentStatus === 'connected',
    // Phase 8a: User memories for personalization
    userMemories,
  });

  // Cost optimization: Trigger pre-flight on journal mount
  // This pre-fetches auth tokens, credits, and context before user clicks Talk
  useEffect(() => {
    if (journalId && geminiLive.performPreFlight && !geminiLive.isPreFlightReady) {
      geminiLive.performPreFlight(journalId);
    }
  }, [journalId, geminiLive.performPreFlight, geminiLive.isPreFlightReady]);

  // Onboarding tour state - show for first-time users
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Voice onboarding state
  const [isOnboardingMode, setIsOnboardingMode] = useState(false);
  // Welcome prompt for voice onboarding (requires user gesture for AudioContext)
  const [showWelcomePrompt, setShowWelcomePrompt] = useState(false);
  // Track if this is a first-time/onboarding user (for mobile inline experience)
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);

  // Check for first-time user onboarding on mount
  useEffect(() => {
    // Skip if already triggered or still loading
    if (autoConnectTriggeredRef.current || isLoading || !journal) {
      return;
    }

    // Check URL directly for initial mount
    const urlParams = new URLSearchParams(window.location.search);
    const isFirstTimeUser = urlParams.get('autoConnect') === 'true';
    const forceOnboarding = urlParams.get('forceOnboarding') === 'true';

    // Also check localStorage to see if they've completed onboarding before
    const hasCompletedOnboarding = localStorage.getItem('onboardingComplete') === 'true';

    // forceOnboarding bypasses the localStorage check (for testing)
    if (forceOnboarding || (isFirstTimeUser && !hasCompletedOnboarding)) {
      autoConnectTriggeredRef.current = true;

      // Clear localStorage if forcing (so it behaves like first time)
      if (forceOnboarding) {
        localStorage.removeItem('onboardingComplete');
      }

      // Remove param from URL without triggering navigation
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Mark as onboarding user (for mobile inline experience)
      setShouldShowOnboarding(true);

      // Show welcome prompt on desktop - mobile uses inline MobileWelcome
      setShowWelcomePrompt(true);
    }
  }, [journal, isLoading]);

  // Handle welcome prompt click - starts voice onboarding
  const handleStartVoiceOnboarding = useCallback(() => {
    setShowWelcomePrompt(false);
    setIsOnboardingMode(true);
    geminiLive.handleConnectAgent('onboarding');
  }, [geminiLive.handleConnectAgent]);

  // Handle skipping voice onboarding
  const handleSkipOnboarding = useCallback(() => {
    setShowWelcomePrompt(false);
    localStorage.setItem('onboardingComplete', 'true');
  }, []);

  // Mark onboarding complete when user disconnects after onboarding session
  useEffect(() => {
    if (isOnboardingMode && geminiLive.agentStatus === 'disconnected') {
      localStorage.setItem('onboardingComplete', 'true');
      setIsOnboardingMode(false);
    }
  }, [isOnboardingMode, geminiLive.agentStatus]);

  // Handle onboarding tour completion
  const handleOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    localStorage.setItem('onboardingComplete', 'true');
  }, []);

  // Calculate user speaking state for VAD feedback
  const isUserSpeaking = !geminiLive.isUserMuted && geminiLive.isAgentActive && geminiLive.isUserSpeakingVad;

  // Calculate agent speaking state (when agent is typing/responding)
  const isAgentSpeaking = geminiLive.isAgentTyping && geminiLive.agentStatus === 'connected';

  // Compute ambient wave state
  const ambientState: AmbientState = useMemo(() => {
    if (geminiLive.agentStatus === 'disconnected') return 'disconnected';
    if (geminiLive.agentStatus === 'connecting' || geminiLive.agentStatus === 'authenticating') return 'connecting';
    if (isUserSpeaking) return 'user-speaking';
    if (isAgentSpeaking) return 'agent-speaking';
    if (geminiLive.agentStatus === 'connected') return 'idle';
    return 'disconnected';
  }, [geminiLive.agentStatus, isUserSpeaking, isAgentSpeaking]);

  // Convert messages for display
  const transcriptMessages = useMemo(() => messages, [messages]);

  // Vision integration callbacks
  const handleCanvasViewportRef = useCallback((getCanvasElFn: () => HTMLElement | null) => {
    setGetCanvasEl(() => getCanvasElFn);
  }, []);

  const handleScreenStreamChange = useCallback((stream: MediaStream | null) => {
    setScreenStream(stream);
  }, []);

  const handleCanvasTabsChange = useCallback((hasContentTabs: boolean) => {
    setHasCanvasContent(hasContentTabs);
  }, []);

  // Toggle transcript visibility
  const handleToggleTranscript = useCallback(() => {
    setIsTranscriptVisible(prev => !prev);
  }, []);

  useEffect(() => {
    if (!isTabletOrMobile) {
      setIsTranscriptVisible(true);
    }
  }, [isTabletOrMobile, journalId]);

  const transcriptPanelWidth = 'clamp(340px, 30vw, 420px)';
  const transcriptPanelPadding = '16px';
  const transcriptPanelOuterWidth = `calc(${transcriptPanelWidth} + (${transcriptPanelPadding} * 2))`;
  const floatingControlsOffset = isTranscriptVisible
    ? `calc(24px + ${transcriptPanelOuterWidth})`
    : '24px';

  // Send text message to agent
  const handleSendMessage = useCallback((message: string) => {
    if (!message.trim()) return;

    // Add user message to transcript
    const userMessage: TranscriptMessage = {
      id: `user-${Date.now()}`,
      content: message.trim(),
      type: 'user',
      source: 'text',
      timestamp: new Date().toISOString(),
    };
    appendMessage(userMessage);

    // Clear input
    setChatInputText('');

    // Send to agent if connected
    if (geminiLive.agentStatus === 'connected' && geminiLive.sendText) {
      geminiLive.sendText(message.trim());
    }
  }, [appendMessage, geminiLive.agentStatus, geminiLive.sendText, setChatInputText]);

  // Handle "Add as Note" from transcript logic
  const handleAddAsNote = useCallback((text: string) => {
    const blockCount = blocks?.length || 0;
    createBlockMutation.mutate({
      journal_id: journalId!,
      block_type: 'note',
      title: 'Note',
      position: blockCount,
      content: { markdown: text }
    });
    toast.success('Saved to journal');
  }, [blocks?.length, createBlockMutation, journalId]);

  if (isLoading) {
    return (
      <AppLayout pageTitle="Loading...">
        <div className="h-full flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </AppLayout>
    );
  }

  if (!journal) {
    return (
      <AppLayout pageTitle="Journal Not Found">
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-slate-600">Journal not found</p>
            <p className="text-sm text-slate-400 mt-2">The journal you're looking for doesn't exist or you don't have access to it.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <>
      {/* Onboarding Tour - rendered outside layout to avoid layout interference */}
      <OnboardingTour run={showOnboarding} onComplete={handleOnboardingComplete} />

      <AppLayout pageTitle={journal.name}>
        <div
          className="h-full bg-[#F8F8F6] flex flex-col chat-container"
          style={{ '--journal-panel-offset': floatingControlsOffset } as CSSProperties}
        >
          <div className="flex-1 min-h-0 flex">
            {isTabletOrMobile ? (
              // Mobile/Tablet Layout - Unified MobileSpaceView
              <MobileSpaceView
                journalId={journalId!}
                journalName={journal.name}
                blocks={(blocks || []).map(b => {
                  // Extract preview text for notes
                  let preview: string | undefined;
                  if (b.block_type === 'note' && b.content) {
                    // BlockNote format: array of blocks with nested content
                    // [{ type: "paragraph", content: [{ type: "text", text: "..." }] }]
                    // Also handles: { markdown: "..." } from AI agent
                    // Also handles: { blocks: [...] } for backward compatibility
                    const extractTextFromBlockNote = (content: unknown): string => {
                      if (!content) return '';
                      if (typeof content === 'string') return content;
                      if (Array.isArray(content)) {
                        return content.map(extractTextFromBlockNote).join(' ').trim();
                      }
                      if (typeof content === 'object') {
                        const obj = content as Record<string, unknown>;
                        // Direct text property (inline content)
                        if (typeof obj.text === 'string') {
                          return obj.text;
                        }
                        // Markdown content from AI agent
                        if (typeof obj.markdown === 'string') {
                          // Extract first meaningful line from markdown
                          const firstLine = obj.markdown.split('\n').find(l => (l as string).trim() !== '');
                          if (firstLine) {
                            return (firstLine as string).replace(/^#+\s*/, '').replace(/\*\*/g, '');
                          }
                          return '';
                        }
                        // Block array wrapper (legacy mobile format)
                        if (Array.isArray(obj.blocks)) {
                          return extractTextFromBlockNote(obj.blocks);
                        }
                        // Nested content array (BlockNote block structure)
                        if (obj.content) {
                          return extractTextFromBlockNote(obj.content);
                        }
                        // Check for html or other formats
                        if (typeof obj.html === 'string') {
                          return obj.html.replace(/<[^>]*>/g, '');
                        }
                      }
                      return '';
                    };
                    const extractedText = extractTextFromBlockNote(b.content);
                    if (extractedText) {
                      preview = extractedText.slice(0, 200);
                    }
                  }
                  return {
                    id: b.id,
                    type: b.block_type === 'explainer' ? 'slide-deck' : b.block_type as MobileBlock['type'],
                    title: b.title,
                    subtitle: undefined,
                    preview,
                    content: b.content,
                  };
                })}
                onCanvasViewportRef={handleCanvasViewportRef}
                agentStatus={geminiLive.agentStatus}
                ambientState={ambientState}
                isUserSpeaking={isUserSpeaking}
                isAgentSpeaking={isAgentSpeaking}
                isUserMuted={geminiLive.isUserMuted}
                audioManager={geminiLive.audioManager}
                messages={messages}
                // Todo integration
                todos={(journal.todo_items as unknown as import('@/types/journal').TodoItem[]) || []}
                onTodoToggle={(id) => {
                  // Toggle todo completion status
                  const currentTodos = (journal.todo_items as unknown as import('@/types/journal').TodoItem[]) || [];
                  const todo = currentTodos.find(t => t.id === id);
                  if (todo) {
                    const updatedTodos = currentTodos.map(t =>
                      t.id === id ? { ...t, completed: !t.completed } : t
                    );
                    // Use Supabase to update
                    import('@/integrations/supabase/client').then(({ supabase }) => {
                      supabase
                        .from('journals')
                        .update({ todo_items: updatedTodos as unknown as import('@/integrations/supabase/types').Json })
                        .eq('id', journalId)
                        .then(() => {
                          queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
                          if (!todo.completed) {
                            fireConfetti();
                          }
                        });
                    });
                  }
                }}
                onTodoAdd={(text) => {
                  const currentTodos = (journal.todo_items as unknown as import('@/types/journal').TodoItem[]) || [];
                  const newTodo = {
                    id: crypto.randomUUID(),
                    text,
                    completed: false,
                    order: currentTodos.length,
                    createdAt: new Date().toISOString(),
                  };
                  const updatedTodos = [...currentTodos, newTodo];
                  import('@/integrations/supabase/client').then(({ supabase }) => {
                    supabase
                      .from('journals')
                      .update({ todo_items: updatedTodos as unknown as import('@/integrations/supabase/types').Json })
                      .eq('id', journalId)
                      .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
                      });
                  });
                }}
                onTodoDelete={(id) => {
                  const currentTodos = (journal.todo_items as unknown as import('@/types/journal').TodoItem[]) || [];
                  const updatedTodos = currentTodos.filter(t => t.id !== id);
                  import('@/integrations/supabase/client').then(({ supabase }) => {
                    supabase
                      .from('journals')
                      .update({ todo_items: updatedTodos as unknown as import('@/integrations/supabase/types').Json })
                      .eq('id', journalId)
                      .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
                      });
                  });
                }}
                onConnect={() => geminiLive.handleConnectAgent((shouldShowOnboarding || isOnboardingMode) ? 'onboarding' : 'general')}
                onDisconnect={geminiLive.handleStopConversation}
                onToggleMute={geminiLive.handleToggleLock}
                onSendText={geminiLive.sendText}
                onAddBlock={async (type, config) => {
                  // Create new block in database
                  const blockCount = blocks?.length || 0;
                  const blockType = (type === 'slide-deck' ? 'explainer' : type) as 'note' | 'quiz' | 'youtube' | 'explainer' | 'pdf';

                  // Handle PDF file upload
                  if (type === 'pdf' && config?.pdfFile) {
                    const file = config.pdfFile;

                    try {
                      toast.info('Uploading PDF...');

                      // Upload to Supabase Storage
                      const { supabase } = await import('@/integrations/supabase/client');

                      // Get current user for storage path (RLS policy requires user ID prefix)
                      const { data: { user } } = await supabase.auth.getUser();
                      if (!user) throw new Error('Not authenticated');

                      // Generate block ID upfront for storage path
                      const blockId = crypto.randomUUID();
                      const fileExt = file.name.split('.').pop() || 'pdf';
                      const storagePath = `${user.id}/${journalId}/${blockId}.${fileExt}`;

                      const { error: uploadError } = await supabase.storage
                        .from('original-pdfs')
                        .upload(storagePath, file, {
                          cacheControl: '3600',
                          upsert: true,
                        });

                      if (uploadError) throw uploadError;

                      // Get signed URL for viewing
                      const { data: signedUrlData, error: signedError } = await supabase.storage
                        .from('original-pdfs')
                        .createSignedUrl(storagePath, 3600); // 1 hour expiry

                      if (signedError) throw signedError;

                      // Create block with PDF content (status: processing)
                      const newBlock = await createBlockMutation.mutateAsync({
                        journal_id: journalId!,
                        block_type: 'pdf',
                        title: file.name.replace(/\.pdf$/i, ''),
                        position: blockCount,
                        content: {
                          status: 'processing',
                          fileName: file.name,
                          fileSize: file.size,
                          storagePath,
                          storageUrl: signedUrlData.signedUrl,
                          analyzing: true,
                        },
                      });

                      // Auto-open the newly uploaded PDF block
                      setAutoExpandBlockId(newBlock.id);

                      toast.success('PDF uploaded! Analyzing...');

                      // Trigger PDF analysis in background
                      const { analyzePDF } = await import('@/services/journalAIService');
                      analyzePDF({
                        journal_id: journalId!,
                        block_id: newBlock.id,
                        storage_path: storagePath,
                        generate_summary: true,
                      }).then(async (analysisResult) => {
                        if (analysisResult.success) {
                          // Update block with analysis results
                          await debouncedContentUpdate(newBlock.id, {
                            status: 'ready',
                            fileName: file.name,
                            fileSize: file.size,
                            storagePath,
                            storageUrl: signedUrlData.signedUrl,
                            analyzing: false,
                            analysis: analysisResult.analysis,
                            pdf_context: analysisResult.pdf_context,
                            text_assets: analysisResult.text_assets,
                          });
                          toast.success('PDF analysis complete!');
                        } else {
                          console.error('PDF analysis failed:', analysisResult.error);
                        }
                      }).catch(err => {
                        console.error('PDF analysis error:', err);
                      });

                    } catch (error) {
                      console.error('PDF upload failed:', error);
                      toast.error('Failed to upload PDF');
                    }
                    return;
                  }

                  // Handle YouTube with URL from config
                  if (type === 'youtube' && config?.youtubeUrl) {
                    const youtubeUrl = config.youtubeUrl;

                    // Fetch video title using oEmbed API (same as desktop pattern)
                    let videoTitle = 'YouTube Video';
                    const videoIdMatch = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/);
                    const videoId = videoIdMatch?.[1];

                    if (videoId) {
                      try {
                        const oembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
                        const response = await fetch(oembedUrl);
                        if (response.ok) {
                          const data = await response.json();
                          videoTitle = data.title || 'YouTube Video';
                        }
                      } catch (error) {
                        console.warn('[YouTube Mobile] Failed to fetch title:', error);
                      }
                    }

                    // Create block with videoUrl + analyzing flag to trigger auto-analysis
                    createBlockMutation.mutate({
                      journal_id: journalId!,
                      block_type: 'youtube',
                      title: videoTitle,
                      position: blockCount,
                      content: { videoUrl: youtubeUrl, analyzing: true },
                    });
                    return;
                  }

                  // Handle Quiz with topic from config
                  if (type === 'quiz' && config?.quizTopic) {
                    createBlockMutation.mutate({
                      journal_id: journalId!,
                      block_type: 'quiz',
                      title: 'Quiz', // Will be updated after generation
                      position: blockCount,
                      content: {
                        generating: true,
                        prompt: config.quizTopic,
                        quizType: 'mcq',
                        questionCount: config.quizCount || 5,
                        difficulty: config.quizDifficulty || 'medium',
                      },
                    });
                    return;
                  }

                  // Handle Explainer with topic from config
                  if ((type === 'explainer' || type === 'slide-deck') && config?.explainerTopic) {
                    createBlockMutation.mutate({
                      journal_id: journalId!,
                      block_type: 'explainer',
                      title: 'Explainer', // Will be updated after generation
                      position: blockCount,
                      content: {
                        generating: true,
                        state: 'generating',
                        topic: config.explainerTopic,
                        duration: 10,
                        outline: null,
                        generatedSlides: [],
                        currentSlideIndex: 0,
                      },
                    });
                    return;
                  }

                  // Default block creation for types without config (e.g., empty note)
                  createBlockMutation.mutate({
                    journal_id: journalId!,
                    block_type: blockType,
                    title: type === 'note' ? 'New Note'
                      : type === 'youtube' ? 'YouTube Video'
                        : type === 'quiz' ? 'Quiz'
                          : type === 'pdf' ? 'PDF Document'
                            : 'New Block',
                    position: blockCount,
                    content: {},
                  });
                }}
                onReorderBlocks={(reordered) => {
                  // Persist new positions to database
                  const updates = reordered.map((b, index) => ({
                    id: b.id,
                    position: index,
                  }));
                  reorderBlocksMutation.mutate(updates);
                }}
                onDeleteBlock={(blockId) => {
                  deleteBlockMutation.mutate(blockId);
                }}
                onContentChange={(blockId, content) => {
                  debouncedContentUpdate(blockId, content as Record<string, unknown>);
                }}
                onBack={() => navigate('/')}
                autoExpandBlockId={autoExpandBlockId}
                isOnboardingMode={shouldShowOnboarding || isOnboardingMode}
              />
            ) : (
              // Desktop Layout - Canvas + Optional Transcript on Right
              <>
                {/* Canvas Column - Left/Main */}
                <div
                  className="flex-1 min-h-0 flex flex-col transition-all duration-300"
                >
                  <SpaceCanvasSection
                    journalId={journalId!}
                    onCanvasViewportRef={handleCanvasViewportRef}
                    onScreenStreamChange={handleScreenStreamChange}
                    onTabsChange={handleCanvasTabsChange}
                    ambientState={ambientState}
                    audioManager={geminiLive.audioManager}
                    pillProps={isTranscriptVisible ? undefined : {
                      agentStatus: geminiLive.agentStatus,
                      isUserSpeaking,
                      isAgentSpeaking,
                      isUserMuted: geminiLive.isUserMuted,
                      onConnect: () => geminiLive.handleConnectAgent('general'),
                      onDisconnect: geminiLive.handleStopConversation,
                      onToggleMute: geminiLive.handleToggleLock,
                      onToggleTranscript: handleToggleTranscript,
                      isTranscriptVisible,
                      showTranscriptToggle: true,
                      orientation: 'vertical',
                    }}
                  />
                </div>

                {/* Transcript Column - Right (flush with canvas, no background) */}
                <div
                  className="transition-all duration-300 ease-in-out flex flex-col min-h-0 flex-none"
                  style={{
                    width: isTranscriptVisible ? transcriptPanelOuterWidth : '0',
                    opacity: isTranscriptVisible ? 1 : 0,
                    overflow: isTranscriptVisible ? 'visible' : 'hidden',
                    marginRight: 0,
                  }}
                >
                  <div className="h-full p-4">
                    <div className="h-full flex flex-col rounded-[28px] bg-[#F8F8F6] border border-slate-200/60 shadow-[0_8px_20px_rgba(15,23,42,0.04)] overflow-hidden">
                      <AgentTranscript
                        messages={transcriptMessages}
                        isAgentConnected={geminiLive.agentStatus === 'connected'}
                        agentStatus={geminiLive.agentStatus}
                        isUserMuted={geminiLive.isUserMuted}
                        isUserSpeaking={isUserSpeaking}
                        isAgentSpeaking={isAgentSpeaking}
                        onConnect={() => geminiLive.handleConnectAgent('general')}
                        onDisconnect={geminiLive.handleStopConversation}
                        onToggleMute={geminiLive.handleToggleLock}
                        onCollapse={handleToggleTranscript}
                        onSendText={handleSendMessage}
                        onAddAsNote={handleAddAsNote}
                        audioManager={geminiLive.audioManager}
                        isExtendingSession={geminiLive.isExtendingSession}
                        isOnboardingMode={shouldShowOnboarding || isOnboardingMode}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </AppLayout>
    </>
  );
};

export default SpaceDetailPage;
